import React from "react";
import { Container } from "react-bootstrap";
import { SKILLS_INVENTORY, SKILL_LEVELS } from "../../data/career-skills";
import {
  Section,
  SectionLabel,
  SectionTitle,
  SectionDesc,
  AuthBar,
  AuthLabel,
  AuthButton,
  SaveStatus,
  SkillRow,
  SkillTopRow,
  SkillName,
  SkillRight,
  LevelButtons,
  LevelBtn,
  ProofBadge,
  ProofToggle,
  ProofPanel,
  ProofRow,
  ProofTabs,
  ProofTab,
  ProofInput,
  ProofSaveBtn,
  ProofExisting,
  SignInProofCTA,
} from "./CareerPath.styles";
import PublicProfileToggle from "./PublicProfileToggle";

const SkillsInventory = ({
  user,
  signIn,
  signOut,
  saveMsg,
  skills,
  setSkills,
  certs,
  openProof,
  proofTab,
  setProofTab,
  proofUrl,
  setProofUrl,
  uploading,
  profilePublic,
  copyHint,
  emailMuted,
  onToggleEmailMute,
  onTogglePublic,
  onCopyShareLink,
  onProofToggle,
  onSaveLink,
  onUploadFile,
  onRemoveCert,
}) => (
  <Section>
    <Container>
      <SectionLabel>SELF-ASSESSMENT</SectionLabel>
      <SectionTitle>Skills inventory</SectionTitle>
      <SectionDesc>
        Be honest with yourself. "Competent" means you can do it without Googling
        every step. "Expert" means other people ask you how.
      </SectionDesc>

      <AuthBar>
        {user ? (
          <>
            <AuthLabel>Signed in as {user.email}{saveMsg && <SaveStatus>{saveMsg}</SaveStatus>}</AuthLabel>
            <AuthButton onClick={signOut}>SIGN OUT</AuthButton>
          </>
        ) : (
          <>
            <AuthLabel>Sign in to save your progress to the cloud</AuthLabel>
            <AuthButton onClick={signIn}>SIGN IN WITH GOOGLE</AuthButton>
          </>
        )}
      </AuthBar>

      {user && (
        <PublicProfileToggle
          profilePublic={profilePublic}
          copyHint={copyHint}
          emailMuted={emailMuted}
          onTogglePublic={onTogglePublic}
          onCopyShareLink={onCopyShareLink}
          onToggleEmailMute={onToggleEmailMute}
        />
      )}

      <div>
        {SKILLS_INVENTORY.map((skill) => {
          const cert = certs[skill];
          const proofOpen = openProof === skill;
          const currentLevel = skills[skill];
          const achieved = currentLevel === "Competent" || currentLevel === "Expert";
          const canProof = achieved || Boolean(cert);
          return (
            <SkillRow key={skill}>
              <SkillTopRow>
                <SkillName>
                  {skill}
                  {cert && <ProofBadge>· proof attached</ProofBadge>}
                </SkillName>
                <SkillRight>
                  <LevelButtons>
                    {SKILL_LEVELS.map((level) => (
                      <LevelBtn
                        key={level}
                        active={currentLevel === level}
                        onClick={() => setSkills(prev => ({ ...prev, [skill]: level }))}
                        aria-label={`${skill}: ${level}`}
                      >
                        {level}
                      </LevelBtn>
                    ))}
                  </LevelButtons>
                  {canProof && (
                    <ProofToggle
                      type="button"
                      onClick={() => onProofToggle(skill)}
                      aria-label={`Proof of work for ${skill}`}
                    >
                      {cert ? "Edit proof" : "Add proof"}
                    </ProofToggle>
                  )}
                </SkillRight>
              </SkillTopRow>

              <ProofPanel open={proofOpen}>
                {!user ? (
                  <ProofRow>
                    <span style={{
                      fontFamily: "'Space Mono', monospace",
                      fontSize: 11,
                      letterSpacing: "0.06em",
                      color: "var(--nd-text-secondary)",
                    }}>
                      Sign in to attach proof
                    </span>
                    <SignInProofCTA type="button" onClick={signIn}>
                      Sign in with Google
                    </SignInProofCTA>
                  </ProofRow>
                ) : (
                  <>
                    <ProofTabs>
                      <ProofTab type="button" active={proofTab === "link"} onClick={() => setProofTab("link")}>
                        Link
                      </ProofTab>
                      <ProofTab type="button" active={proofTab === "file"} onClick={() => setProofTab("file")}>
                        File
                      </ProofTab>
                    </ProofTabs>
                    {proofTab === "link" ? (
                      <ProofRow>
                        <ProofInput
                          type="url"
                          placeholder="https://… certification or repo URL"
                          value={proofUrl}
                          onChange={(e) => setProofUrl(e.target.value)}
                        />
                        <ProofSaveBtn
                          type="button"
                          disabled={!proofUrl}
                          onClick={() => onSaveLink(skill)}
                        >
                          Save
                        </ProofSaveBtn>
                      </ProofRow>
                    ) : (
                      <ProofRow>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.webp"
                          style={{
                            fontFamily: "'Space Mono', monospace",
                            fontSize: 12,
                            color: "var(--nd-text-secondary)",
                          }}
                          onChange={(e) => onUploadFile(skill, e.target.files[0])}
                        />
                        {uploading && (
                          <span style={{
                            fontFamily: "'Space Mono', monospace",
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            color: "var(--nd-text-disabled)",
                          }}>
                            Uploading…
                          </span>
                        )}
                      </ProofRow>
                    )}
                    {cert && (
                      <ProofRow style={{ marginTop: 12 }}>
                        <ProofExisting
                          href={cert.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {cert.type === "file" ? "↗ " : "↗ "}
                          {cert.name}
                        </ProofExisting>
                        <ProofSaveBtn
                          type="button"
                          onClick={() => onRemoveCert(skill)}
                          style={{ background: "transparent", color: "var(--nd-accent)", border: "1px solid var(--nd-accent)" }}
                        >
                          Remove
                        </ProofSaveBtn>
                      </ProofRow>
                    )}
                  </>
                )}
              </ProofPanel>
            </SkillRow>
          );
        })}
      </div>
    </Container>
  </Section>
);

export default SkillsInventory;
